import { ExpandableToFullHeight } from "@faire/retailer-visitor-shared/components/ExpandableToFullHeight/ExpandableToFullHeight";
import { FilterInfoSticky } from "@faire/retailer-visitor-shared/components/Filters/FilterInfoSticky";
import { FilterName } from "@faire/retailer-visitor-shared/components/Filters/FilterName";
import { MAX_WIDE_SCREEN_CONTENT_WIDTH } from "@faire/retailer-visitor-shared/features/Marketplace/styleConsts";
import { setTimeoutIfPossible } from "@faire/retailer-visitor-shared/lib/async-functions/setTimeoutIfPossible";
import { OuterResultsContentWrapper } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/CategoryPageShareables/CategoryPageCommonStyles";
import { CategoryNextPagePrefetch } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/CategoryNextPagePrefetch";
import { CategoryPageSidebar } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/CategoryPageSidebar";
import { useIsCategoryPageFiltersLoading } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/DataFetching/useIsCategoryPageFiltersLoading";
import { CategoryPageLayoutElements } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/LayoutElements/CategoryPageLayoutElements";
import { useGetCategoryPageLayoutElementsByPlacement } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/LayoutElements/useGetCategoryPageLayoutElementsByPlacement";
import { CategoryPageFilters } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/MainContent/__internal__/FiltersAndContent/__internal__/CategoryPageFilters";
import { CategoryPageFilterStoreProvider } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/MainContent/__internal__/FiltersAndContent/__internal__/CategoryPageFilterStoreProvider";
import { Title } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/MainContent/__internal__/FiltersAndContent/__internal__/Title";
import { useCategoryPageHeaderAndSubheaderTexts } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/MainContent/__internal__/HeaderAndSubheader/useCategoryPageHeaderAndSubheaderTexts";
import { CategoryPagePagination } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/MainContent/__internal__/Pagination/CategoryPagePagination";
import { CategoryPageProductOrBrandGrid } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/MainContent/__internal__/ProductOrBrandGrid/CategoryPageProductOrBrandGrid";
import { CategoryPageRetrievalQueries } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/MainContent/__internal__/RetrievalQueries/CategoryPageRetrievalQueries";
import { useIsBestsellers } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/useIsBestsellers";
import { useIsFullWidthCategory } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/useIsFullWidthCategory";
import { useIsHubCategoryLoadingStrategy } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/useIsHubCategoryLoadingStrategy";
import { useCurrentNavigationPath } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/useCurrentNavigationPath";
import { ElevatedContentWrapper } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/LoggedInCategoryPageStyles";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { WhenVisibilityIsImminent } from "@faire/web--source/ui/VisibilitySensor/WhenVisibilityIsImminent";
import { trackCategoriesPaginationClick } from "@faire/web-api--source/events/categories/click/pagination";
import { ILayoutElement } from "@faire/web-api--source/indigofair/data/ILayoutElement";
import React, { useState } from "react";
import styled, { css } from "styled-components";

import Placement = ILayoutElement.Placement;

export const CategoryPageFiltersAndContent = ({
  scrollToTop,
  scrollRef,
  pageLoadTimingRef,
  className,
}: {
  scrollToTop: (params: { toResults?: boolean; toTop?: boolean }) => void;
  filterRef: React.Ref<HTMLDivElement | null>;
  scrollRef: React.Ref<HTMLDivElement | null>;
  pageLoadTimingRef?: React.Ref<HTMLDivElement>;
  className?: string;
}) => {
  const isFiltersLoading = useIsCategoryPageFiltersLoading();

  const useHubUPLTMeasurement = useIsHubCategoryLoadingStrategy();

  const navigationPath = useCurrentNavigationPath();

  const getCategoryPageLayoutElementsByPlacement =
    useGetCategoryPageLayoutElementsByPlacement();

  const layoutElementsBelowBreadcrumb =
    getCategoryPageLayoutElementsByPlacement(Placement.BELOW_BREADCRUMB);

  const hasFullWidthBannerBelowBreadcrumb =
    layoutElementsBelowBreadcrumb[0]?.type ===
      ILayoutElement.Type.FULL_WIDTH_IMAGE_BANNER ||
    layoutElementsBelowBreadcrumb[0]?.type ===
      ILayoutElement.Type.MID_PRICE_POINT_PLUS_CATEGORY_HOME_PAGE_HEADER_BANNER;

  const shouldShowContentTopPadding = !layoutElementsBelowBreadcrumb.some(
    (element) => element.type !== ILayoutElement.Type.NO_OP_LAYOUT_ELEMENT
  );

  const { headerText, subheaderText, gridHeaderText } =
    useCategoryPageHeaderAndSubheaderTexts();

  const isFullWidthCategory = useIsFullWidthCategory();
  const { isBestsellers, isBestsellersProductCategory } = useIsBestsellers();

  const shouldShowTopPageTitle =
    layoutElementsBelowBreadcrumb.length > 0 &&
    !hasFullWidthBannerBelowBreadcrumb &&
    isFullWidthCategory;

  const [isScrollingToTop, setIsScrollingToTop] = useState(false);

  return (
    <CategoryPageFilterStoreProvider>
      {shouldShowTopPageTitle ? (
        <PageTitleRow>
          <Title
            titleVariant="pageHeaderSerifRegular"
            title={headerText}
            subtitle={subheaderText}
          />
        </PageTitleRow>
      ) : null}
      <CategoryPageLayoutElements
        placement={ILayoutElement.Placement.BELOW_BREADCRUMB}
        hubCategoryPageLoadTimingRef={
          useHubUPLTMeasurement ? pageLoadTimingRef : undefined
        }
      />
      {isBestsellers &&
      !isBestsellersProductCategory &&
      layoutElementsBelowBreadcrumb.length > 0 ? (
        <Spacer size="6x" />
      ) : null}
      <ElevatedContentWrapper
        $maxWidth={
          isFullWidthCategory ? MAX_WIDE_SCREEN_CONTENT_WIDTH : undefined
        }
      >
        <ContentContainer $includeTopPadding={shouldShowContentTopPadding}>
          <SidebarContainerWithFilters>
            <ExpandableToFullHeight>
              <CategoryPageSidebar />
            </ExpandableToFullHeight>
            <CategoryPageFilters />
          </SidebarContainerWithFilters>
          <StyledOuterResultsContentWrapper
            ref={scrollRef}
            className={className}
            $omitRightPadding={isFullWidthCategory}
          >
            <CategoryPageLayoutElements
              placement={ILayoutElement.Placement.ABOVE_TITLE}
              hubCategoryPageLoadTimingRef={
                useHubUPLTMeasurement ? pageLoadTimingRef : undefined
              }
            />
            <HeaderRow>
              <StyledTitle
                titleVariant="pageHeaderSerifRegular"
                title={gridHeaderText ?? headerText}
                subtitle={subheaderText}
                shouldHideTextTag={shouldShowTopPageTitle}
              />
            </HeaderRow>
            <CategoryPageLayoutElements
              placement={ILayoutElement.Placement.BELOW_TITLE}
              hubCategoryPageLoadTimingRef={
                useHubUPLTMeasurement ? pageLoadTimingRef : undefined
              }
            />
            <StyledFilterInfoSticky
              name={FilterName.LOGGED_IN_CATEGORY}
              isLoading={isFiltersLoading}
              shouldShowSort
              shouldEnableScrollToProducts
            />
            <CategoryPageRetrievalQueries />
            <CategoryPageProductOrBrandGrid
              categoryPageLoadTimingRef={
                useHubUPLTMeasurement ? undefined : pageLoadTimingRef
              }
              isScrollingToTop={isScrollingToTop}
              onFirstBatchVisible={() => setIsScrollingToTop(false)}
            />
          </StyledOuterResultsContentWrapper>
        </ContentContainer>
        <ContentContainer>
          <StyledOuterResultsContentWrapper
            $omitRightPadding={isFullWidthCategory}
            style={{ minHeight: 0, paddingBottom: 0 }}
          >
            <CategoryNextPagePrefetch />
            <WhenVisibilityIsImminent>
              <CategoryPagePagination
                onChangePage={(page) => {
                  trackCategoriesPaginationClick(
                    navigationPath,
                    page.toString()
                  );

                  setIsScrollingToTop(true);
                  setTimeoutIfPossible(() => {
                    setIsScrollingToTop(false);
                  }, 1000);
                  scrollToTop({ toResults: false, toTop: page === 1 });
                }}
              />
              <CategoryPageLayoutElements
                placement={ILayoutElement.Placement.ABOVE_FOOTER}
              />
            </WhenVisibilityIsImminent>
          </StyledOuterResultsContentWrapper>
        </ContentContainer>
      </ElevatedContentWrapper>
    </CategoryPageFilterStoreProvider>
  );
};

const HeaderRow = styled(Row).attrs({
  align: "center",
})`
  padding-bottom: ${getSpacing("1x")};
`;

// eslint-disable-next-line @faire/prefer-slate-attrs -- use ResponsiveContentV2
const SidebarContainerWithFilters = styled(Column)`
  flex: 0 0 228px;
  margin-right: ${getSpacing("6x")};

  &&& {
    ${responsiveMediaQueries.tabletAndBelow} {
      display: none;
    }
  }
`;

const ContentContainer = styled(Row)<{ $includeTopPadding?: boolean }>`
  height: 100%;
  ${({ $includeTopPadding }) =>
    $includeTopPadding
      ? css`
          padding-top: ${getSpacing("1x")};
        `
      : ""}
`;

const StyledTitle = styled(Title)`
  padding-top: 2px;
`;

const StyledFilterInfoSticky = styled(FilterInfoSticky)`
  ${responsiveMediaQueries.tabletAndBelow} {
    width: unset;
  }
`;

const StyledOuterResultsContentWrapper = styled(OuterResultsContentWrapper)<{
  $omitRightPadding?: boolean;
}>`
  ${({ $omitRightPadding }) =>
    $omitRightPadding
      ? css`
          &&& {
            ${responsiveMediaQueries.desktopAndAbove} {
              padding-right: 0;
            }
          }
        `
      : ""}
`;

const PageTitleRow = styled(Row)`
  &&& {
    width: 100%;
    max-width: ${MAX_WIDE_SCREEN_CONTENT_WIDTH}px;
    margin: auto;

    ${responsiveMediaQueries.mobile} {
      padding: ${getSpacing("2x")} ${getSpacing("2x")} 0px;
    }

    ${responsiveMediaQueries.tabletAndAbove} {
      padding: ${getSpacing("3x")} ${getSpacing("6x")} 0px;
    }
  }
`;
